/*
 * Copyright (c) 2022 SARL SIGMA POLYNESIA
 */

import {
  TYPE_APARTMENT,
  TYPE_COMMERCIAL_PREMISE,
  TYPE_COMMERCIAL_PROPERTY,
  TYPE_HOUSE,
  TYPE_OFFICE,
  TYPE_TERRAIN
} from '../../enums/EstateTypeCodes.mjs'

/**
 * Returns the generated estate reference.
 * @param {string} transaction
 * @param {string} type
 * @param {number} id
 * @returns {string}
 */
export function generateEstateReference (transaction, type, id) {
  return [transaction, type, id].join('')
}

/**
 * Returns the estate rooms category (F1, F2...).
 * @param estate
 * @returns {null|string}
 */
export function getEstateCategory (estate) {
  const { bedrooms, livingRooms } = estate

  if (bedrooms > 0 || livingRooms > 0) {
    return `F${bedrooms + livingRooms}`
  }
  return null
}

/**
 * Returns a generated description for an estate.
 * todo translate text
 * @param estate
 * @returns {string}
 */
export function getEstateGeneratedTitle (estate) {
  const result = []

  // City
  if (estate.City && estate.City.name) {
    result.push(estate.City.name)
  }

  // Type
  if (estate.type === TYPE_APARTMENT) {
    result.push('Appartement')
  } else if (estate.type === TYPE_HOUSE) {
    result.push('Maison')
  } else if (estate.type === TYPE_TERRAIN) {
    result.push('Terrain')
  } else if (estate.type === TYPE_OFFICE) {
    result.push('Bureaux')
  } else if (estate.type === TYPE_COMMERCIAL_PREMISE) {
    result.push('Local')
  } else if (estate.type === TYPE_COMMERCIAL_PROPERTY) {
    result.push('Fond de commerce')
  }

  // Category
  const category = getEstateCategory(estate)

  if (category) {
    result.push(category)
  }

  // Building surface.
  if (estate.surface > 0) {
    result.push(`${estate.surface} m²`)
  }

  // Terrain surface.
  if (estate.landArea > 0) {
    if (estate.type === TYPE_TERRAIN) {
      result.push(`${estate.landArea} m²`)
    } else {
      result.push(`terrain ${estate.landArea} m²`)
    }
  }

  // Floors.
  if (estate.floors > 1) {
    result.push(`${estate.floors} étages`)
  }

  // Bedrooms.
  if (estate.bedrooms > 1) {
    result.push(`${estate.bedrooms} chambres`)
  } else if (estate.bedrooms > 0) {
    result.push(`${estate.bedrooms} chambre`)
  }

  // Bathrooms.
  if (estate.bathrooms > 0) {
    result.push(`${estate.bathrooms} sdb`)
  }

  // Toilets.
  if (estate.toilets > 0) {
    result.push(`${estate.toilets} WC`)
  }

  // Kitchens.
  if (estate.kitchens > 1) {
    result.push(`${estate.kitchens} cuisines`)
  } else if (estate.kitchens > 0) {
    result.push(`${estate.kitchens} cuisine`)
  }

  // Parkings.
  if (estate.parking > 1) {
    result.push(`${estate.parking} parkings`)
  } else if (estate.parking > 0) {
    result.push(`${estate.parking} parking`)
  }

  // Residence
  if (estate.residence) {
    result.push(estate.residence)
  }

  // Parcel
  if (estate.parcel) {
    result.push(`Lot ${estate.parcel}`)
  }

  // Furnished.
  if (estate.furnished) {
    result.push('meublé')
  }

  // Equipped.
  if (estate.furnished) {
    result.push('équipé')
  }

  return result
    .filter((el) => el != null)
    .join(' - ')
}

/**
 * Returns the estate address.
 * @param estate
 * @returns {string}
 */
export function getEstateAddress (estate) {
  const out = []

  if (estate.City?.name) {
    out.push(estate.City.name)
  }
  if (estate.residence) {
    out.push(estate.residence)
  }
  if (estate.parcel) {
    out.push(`Lot ${estate.parcel}`) // todo translate
  }
  return out.join(', ')
}

/**
 * Returns the estate formal description or a generated description.
 * @param estate
 * @returns {*|string}
 */
export function getEstateDescription (estate) {
  if (estate.formalDescription?.length > 0) {
    return estate.formalDescription?.replace(/\r?\n/g, ', ')
  }
  return getEstateGeneratedTitle(estate)
}

/**
 * Returns the estate highlighted photo or the first photo.
 * @param estate
 * @returns {T|*}
 */
export function getEstatePhoto (estate) {
  const photos = (estate.Photos || [])
  return photos.find((el) => el.highlighted) || photos[0]
}
